// ===========================================
// Conversation List Component
// ===========================================
// Displays list of conversations with unread badges

import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Avatar,
  Badge,
  Typography,
  Skeleton,
  Alert,
  Box,
  Chip,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { Conversation, getRoleColor, messagingLabel } from '../../api/messages.api';
import { StudentAvatar } from '../student';

// ===========================================
// TYPES
// ===========================================

interface ConversationListProps {
  conversations: Conversation[];
  selectedId: string | null;
  loading?: boolean;
  onSelect: (conversationId: string) => void;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function truncateMessage(message: string, maxLength: number = 50): string {
  if (message.length <= maxLength) return message;
  return `${message.substring(0, maxLength)}...`;
}

/**
 * Get the avatar background color for a participant
 * Uses teacher's custom color if available, otherwise falls back to role color
 */
function getAvatarBgColor(participant: {
  role: 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';
  color?: string | null;
}): string {
  // Use custom teacher color if available
  if (participant.color) {
    return participant.color;
  }
  // Fallback to role-based color
  return `${getRoleColor(participant.role)}.main`;
}

// ===========================================
// COMPONENT
// ===========================================

export default function ConversationList({
  conversations,
  selectedId,
  loading = false,
  onSelect,
}: ConversationListProps) {
  if (loading) {
    return (
      <List disablePadding>
        {[1, 2, 3].map((i) => (
          <ListItem key={i} divider>
            <ListItemAvatar>
              <Skeleton variant="circular" width={40} height={40} />
            </ListItemAvatar>
            <ListItemText
              primary={<Skeleton width="60%" />}
              secondary={<Skeleton width="80%" />}
            />
          </ListItem>
        ))}
      </List>
    );
  }

  if (conversations.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No conversations yet. Start a new message!</Alert>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {conversations.map((conversation) => {
        const { otherParticipant, lastMessage, unreadCount } = conversation;
        const isSelected = selectedId === conversation.id;

        return (
          <ListItem
            key={conversation.id}
            disablePadding
            divider
            sx={{
              bgcolor: isSelected ? 'action.selected' : 'transparent',
            }}
          >
            <ListItemButton onClick={() => onSelect(conversation.id)}>
              <ListItemAvatar>
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  overlap="circular"
                  invisible={unreadCount === 0}
                >
                  {otherParticipant.role === 'STUDENT' && otherParticipant.birthDate ? (
                    <StudentAvatar
                      avatar={otherParticipant.avatar || null}
                      birthDate={otherParticipant.birthDate}
                      firstName={otherParticipant.firstName}
                      lastName={otherParticipant.lastName}
                      size={40}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        bgcolor: getAvatarBgColor(otherParticipant),
                      }}
                    >
                      {getInitials(otherParticipant.firstName, otherParticipant.lastName)}
                    </Avatar>
                  )}
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body1"
                      fontWeight={unreadCount > 0 ? 600 : 400}
                      noWrap
                    >
                      {otherParticipant.firstName} {otherParticipant.lastName}
                    </Typography>
                    <Chip
                      label={messagingLabel(otherParticipant)}
                      size="small"
                      color={getRoleColor(otherParticipant.isOffice ? 'ADMIN' : otherParticipant.role)}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                secondary={
                  <Box
                    component="span"
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{
                        flex: 1,
                        fontWeight: unreadCount > 0 ? 500 : 400,
                      }}
                    >
                      {lastMessage
                        ? truncateMessage(lastMessage.content)
                        : 'No messages yet'}
                    </Typography>
                    {conversation.lastMessageAt && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1, flexShrink: 0 }}
                      >
                        {formatDistanceToNow(new Date(conversation.lastMessageAt), {
                          addSuffix: true,
                        })}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}
